
import type { AuditResult, CategoryScore } from '../../types/audit';
import brandData from '../../brand_name_database.json';

// --- Helpers ---

const countSyllables = (word: string): number => {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
};

const getGrade = (score: number): string => {
  if (score >= 97) return 'A+';
  if (score >= 93) return 'A';
  if (score >= 90) return 'A-';
  if (score >= 87) return 'B+';
  if (score >= 83) return 'B';
  if (score >= 80) return 'B-';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
};

const getRiskLevel = (score: number): 'Low' | 'Medium' | 'High' => {
  if (score >= 80) return 'Low';
  if (score >= 60) return 'Medium';
  return 'High';
};

// --- Category Logic ---

const scoreLinguistics = (name: string): CategoryScore => {
  let score = 100;
  const issues: string[] = [];
  const strengths: string[] = [];
  const cleanName = name.trim();
  
  // Character Count (Relaxed for modern web standards)
  if (cleanName.length >= 3 && cleanName.length <= 9) {
    strengths.push("Excellent length (3-9 chars).");
  } else if (cleanName.length <= 15) {
    score -= 15;
    issues.push("Moderate length (10-15 chars).");
  } else {
    score -= 40;
    issues.push("Name is quite long (16+ chars).");
  }

  // Syllable Count (Relaxed)
  const syllables = countSyllables(cleanName);
  if (syllables <= 3) {
    strengths.push("Punchy flow (1-3 syllables).");
  } else if (syllables === 4) {
    score -= 10;
    issues.push("Good flow, slightly complex (4 syllables).");
  } else {
    score -= 30;
    issues.push("Complex flow (5+ syllables).");
  }

  // Word Count
  const wordCount = cleanName.split(/[\s-.]+/).length; 
  if (wordCount === 1) {
    strengths.push("Single word brand (High recall).");
  } else if (wordCount === 2) {
    strengths.push("Two-word composite.");
  } else {
    score -= 20;
    issues.push("Multi-word name (3+ words).");
  }

  const finalScore = Math.max(0, score);
  
  return { 
    score: finalScore, 
    grade: getGrade(finalScore), 
    issues, 
    strengths,
    riskLevel: getRiskLevel(finalScore),
    whyItMatters: "Short, single-word brands reduce cognitive load, improve recall, and are significantly cheaper to market on visual channels.",
    guidance: "If you are early-stage, brevity is a competitive moat. If the name is long, establish a canonical nickname immediately.",
    benchmark: "Top 100 global brands average 2.4 syllables and 6 characters in length."
  };
};

const scorePhonetics = (name: string): CategoryScore => {
  let score = 100;
  const issues: string[] = [];
  const strengths: string[] = [];
  const lower = name.toLowerCase();

  // Spelling Safety
  if (/(.)\1/.test(lower)) { 
     if (/[jqx]{2}/.test(lower)) {
         score -= 20;
         issues.push("Difficult double consonants.");
     }
  }
  
  // Silent starts
  if (/^(kn|ph|wr|ps|gn)/.test(lower)) {
      score -= 15;
      issues.push("Silent start letters (Check spelling recall).");
  }

  // Friction Detection
  const frictionMatch = lower.match(/[xzqy]/g);
  if (frictionMatch && frictionMatch.length > 2) { 
      score -= 20;
      issues.push("High phonetic friction (Too many complex sounds).");
  } else {
      strengths.push("Phonetically balanced.");
  }

  const finalScore = Math.max(0, score);

  return { 
    score: finalScore, 
    grade: getGrade(finalScore), 
    issues, 
    strengths,
    riskLevel: getRiskLevel(finalScore),
    whyItMatters: "Clear pronunciation is critical for word-of-mouth growth and voice search (Siri/Alexa) optimization.",
    guidance: "Perform the 'Radio Test': If heard on a podcast, would a customer know exactly how to spell it?",
    benchmark: "Brands with high phonetic clarity see 20% higher direct traffic retention."
  };
};

const scoreDigital = (name: string): CategoryScore => {
  let score = 100;
  const issues: string[] = [];
  const strengths: string[] = [];
  
  // Calibrated: Allow dots and hyphens as neutral/safe
  const unsafeChars = name.match(/[^a-zA-Z0-9.-]/g);
  const hasSpace = /\s/.test(name);

  if (!unsafeChars && !hasSpace) {
      strengths.push("Digital ready (URL safe).");
  } else {
      if (hasSpace) {
          score -= 20;
          issues.push("Contains spaces (Remove for social handles).");
      }
      if (unsafeChars) {
          score -= 50;
          issues.push("Contains unsafe characters for URLs/Handles.");
      }
  }

  // Visual Width (Minor check)
  const wideCount = (name.match(/[mwMW]/g) || []).length;
  if (wideCount > 3) {
      score -= 10;
      issues.push("Visually heavy characters.");
  }

  const finalScore = Math.max(0, score);

  return { 
    score: finalScore, 
    grade: getGrade(finalScore), 
    issues, 
    strengths,
    riskLevel: getRiskLevel(finalScore),
    whyItMatters: "Digital readiness determines how easily you can exist on social platforms, URLs, and app stores without user friction.",
    guidance: "If the .com is unavailable, ensure your social handles are identical across all platforms to maintain trust.",
    benchmark: "95% of unicorns launch with a URL-safe, special-character-free name."
  };
};

const scoreStructural = (name: string): CategoryScore => {
  let score = 100;
  const issues: string[] = [];
  const strengths: string[] = [];
  const lower = name.toLowerCase().replace(/[^a-z]/g, '');
  
  // Fix E: Single-Letter & Acronym Handling
  if (name.length <= 3 || /^[A-Z0-9]+$/.test(name)) {
      return { 
          score: 100, 
          grade: 'A+', 
          issues: [], 
          strengths: ["Compact structure (Acronym/Short)."],
          riskLevel: 'Low',
          whyItMatters: "Structural integrity dictates logo balance and visual weight on packaging and screens.",
          guidance: "Short, structural names allow for bolder, larger typography in logo design.",
          benchmark: "Acronyms and 3-letter brands represent the highest density of trademark value."
      };
  }

  // Vowel Ratio (Calibrated)
  const vowels = (lower.match(/[aeiou]/g) || []).length;
  const ratio = vowels / (lower.length || 1);
  
  if (ratio < 0.20 || ratio > 0.70) { 
      score -= 20;
      issues.push(`Unusual vowel structure (${Math.round(ratio * 100)}%).`);
  } else {
      strengths.push("Balanced Vowel/Consonant ratio.");
  }

  // Repetition Check
  if (/(.)\1\1/.test(lower)) {
      score -= 30;
      issues.push("Triple letter repetition detected.");
  }

  const finalScore = Math.max(0, score);

  return { 
    score: finalScore, 
    grade: getGrade(finalScore), 
    issues, 
    strengths,
    riskLevel: getRiskLevel(finalScore),
    whyItMatters: "Visual symmetry and character balance subconsciously signal trustworthiness and professionalism.",
    guidance: "Avoid triple repeating letters. Ensure the visual 'shape' of the word looks pleasing when typed in sans-serif.",
    benchmark: "Balanced consonant-vowel structures are 3x more likely to be perceived as 'premium'."
  };
};

const scoreStrategic = (name: string, industry: string): CategoryScore => {
  let score = 50; // Start at Neutral
  const issues: string[] = [];
  const strengths: string[] = [];
  
  const lowerName = name.toLowerCase();
  
  // Get keywords from DB
  const industryKey = industry.toLowerCase();
  // @ts-ignore
  const dbKeywords = brandData.industries[industryKey] || [];
  
  const matches = dbKeywords.some((kw: string) => lowerName.includes(kw));
  
  if (matches) {
      score = 100;
      strengths.push("Contains high-value industry keyword.");
  } else {
      issues.push("Abstract name (No direct keyword match).");
  }

  const finalScore = Math.max(0, score);

  return { 
    score: finalScore, 
    grade: getGrade(finalScore), 
    issues, 
    strengths,
    riskLevel: getRiskLevel(finalScore),
    whyItMatters: "Strategic alignment determines how much marketing spend is needed to explain 'what you do'.",
    guidance: "Abstract names require higher initial marketing spend to build meaning. Descriptive names convert faster early on.",
    benchmark: "Descriptive brands see 15% higher CTR in early-stage paid ads."
  };
};

// --- Main Calculation ---

export const calculateBrandScore = (name: string, industry: string): AuditResult => {
  // 1. Base Scores
  let linguistics = scoreLinguistics(name);
  let phonetics = scorePhonetics(name);
  let digital = scoreDigital(name);
  let structural = scoreStructural(name);
  let strategic = scoreStrategic(name, industry);
  
  let brandType: AuditResult['brandType'] = 'Compound';
  let overrideApplied = false;

  // 2. Intelligent Overrides
  const lowerName = name.toLowerCase();
  
  // Fix A: Abstract Premium Logic
  if (name.length <= 9 && linguistics.score >= 70 && structural.score >= 70) {
      if (strategic.score < 100) {
          strategic.score = 100;
          strategic.strengths.push("Premium Abstract Brand Strategy.");
          strategic.issues = []; 
          brandType = 'Abstract Premium';
          overrideApplied = true;
          // Update guidance for override
          strategic.guidance = "Your abstract name is strong enough to function as a premium brand vessel (like Nike or Apple).";
      }
  }

  // Fix D: Legacy / Descriptive Brand Override
  const seoTerms = ["seo", "group", "services", "solutions", "trader", "depot", "market", "store", "buy", "best"];
  // @ts-ignore
  const dbKeywords = brandData.industries[industry.toLowerCase()] || [];
  
  const isKeywordRich = seoTerms.some(t => lowerName.includes(t)) || dbKeywords.some((k: string) => lowerName.includes(k));

  if (isKeywordRich) {
      brandType = 'Descriptive';
      if (linguistics.score < 85) {
          linguistics.score = Math.max(linguistics.score + 20, 85);
          linguistics.strengths.push("Descriptive clarity outweighs length.");
          linguistics.issues = linguistics.issues.filter(i => !i.includes("length") && !i.includes("syllable") && !i.includes("word"));
          overrideApplied = true;
          linguistics.guidance = "For descriptive brands, length is acceptable as long as the value proposition is clear.";
      }
      strategic.score = 100; 
      strategic.issues = [];
      if (!strategic.strengths.includes("Contains high-value industry keyword.")) {
          strategic.strengths.push("High descriptive value.");
      }
  }

  // 3. Weighted Average
  const overallScore = Math.round(
      (linguistics.score * 0.30) +
      (phonetics.score * 0.25) +
      (digital.score * 0.20) +
      (structural.score * 0.15) +
      (strategic.score * 0.10)
  );

  // Fix F: Grade Consistency
  if (overrideApplied) {
      if (brandType === 'Abstract Premium' && strategic.score === 100) strategic.grade = 'A+ (Strategy)';
      if (brandType === 'Descriptive' && linguistics.score >= 85) linguistics.grade = 'A (Descriptive)';
  } else {
      linguistics.grade = getGrade(linguistics.score);
      strategic.grade = getGrade(strategic.score);
  }
  
  digital.grade = getGrade(digital.score);
  phonetics.grade = getGrade(phonetics.score);
  structural.grade = getGrade(structural.score);

  // 4. Consultant Actions Generation
  const actions: string[] = [];
  
  // Prioritize actions based on lowest scores
  const categories = [
      { name: 'Digital', score: digital.score, risk: 'Secure exact match social handles immediately or pivot name.' },
      { name: 'Phonetics', score: phonetics.score, risk: 'Run a "radio test" with 10 people to verify spelling accuracy.' },
      { name: 'Structural', score: structural.score, risk: 'Consult a logo designer to ensure visual balance before trademarking.' },
      { name: 'Linguistics', score: linguistics.score, risk: 'Consider a shorter nickname or acronym for daily use.' },
      { name: 'Strategic', score: strategic.score, risk: 'Develop a strong tagline to clarify your industry positioning.' }
  ];
  
  // Sort by score ascending (worst first)
  categories.sort((a, b) => a.score - b.score);
  
  // Take top 3 relevant actions from worst categories
  categories.slice(0, 3).forEach(cat => {
      if (cat.score < 85) {
          actions.push(cat.risk);
      }
  });
  
  if (actions.length === 0) {
      actions.push("Proceed to trademark search.");
      actions.push("Secure .com and .io domains.");
      actions.push("Lock down social handles on Twitter/Instagram.");
  }

  // 5. Consultant Summary Generation
  const sentiment = overallScore >= 90 ? "exceptional" : overallScore >= 80 ? "strong" : "developing";
  const strengthSummary = categories.sort((a, b) => b.score - a.score)[0].name; // Best category
  const riskSummary = categories.sort((a, b) => a.score - b.score)[0]; // Worst category
  
  const consultantSummary = `The brand '${name}' presents a ${sentiment} profile with a '${brandType}' classification. Its primary asset is ${strengthSummary} strength.${riskSummary.score < 80 ? ` However, strategic attention is needed regarding ${riskSummary.name} to mitigate risk.` : ''} Overall, this foundation is suitable for ${sentiment === 'exceptional' ? 'global scale' : 'market entry'} with the recommended refinements.`;

  return {
      overallScore,
      grade: getGrade(overallScore),
      brandType,
      categories: {
          linguistics,
          phonetics,
          digital,
          structural,
          strategic
      },
      feedback: {
          strengths: [
              ...linguistics.strengths,
              ...phonetics.strengths,
              ...digital.strengths,
              ...structural.strengths,
              ...strategic.strengths
          ],
          risks: [
              ...linguistics.issues,
              ...phonetics.issues,
              ...digital.issues,
              ...structural.issues,
              ...strategic.issues
          ],
          actions
      },
      inputs: { name, industry },
      timestamp: new Date().toISOString(),
      consultantSummary
  };
};
